import React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Chip,
  Alert,
  Divider,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PaymentIcon from "@mui/icons-material/Payment";
import { FriendlyButton } from "../shared";

/**
 * PaymentMethodsList Component
 * Displays saved payment methods and allows selection or adding new ones
 */
const PaymentMethodsList = ({
  t,
  onSelect,
  cardPaymentAvailable = true,
  achPaymentAvailable = true,
  cards = [],
  banks = [],
}) => {
  const hasSavedMethods = cards.length > 0 || banks.length > 0;
  const hasCards = cards.length > 0;
  const hasBanks = banks.length > 0;

  return (
    <Box sx={{ maxWidth: 600, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "primary.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <PaymentIcon sx={{ fontSize: 40, color: "primary.main" }} />
        </Box>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}
        >
          {t("select_payment_method", "Select Payment Method")}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {hasSavedMethods 
            ? t("choose_saved_or_new", "Choose a saved method or add a new one")
            : t("add_payment_method", "Add a payment method to continue")
          }
        </Typography>
      </Box>

      {/* Saved Cards */}
      {hasCards && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t("saved_cards", "Saved Cards")}
          </Typography>
          <Stack spacing={1.5}>
            {cards.map((card) => (
              <Paper
                key={card._id}
                variant="outlined"
                onClick={() => onSelect({
                  id: card._id,
                  type: "card",
                  isNew: false,
                  last4: card.lastNumbers || card.last4,
                  brand: card.brand,
                })}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  borderWidth: 2,
                  borderColor: "grey.300",
                  borderRadius: 2,
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "primary.50",
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: "grey.100",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CreditCardIcon sx={{ fontSize: 24, color: "grey.600" }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {card.brand || t("card", "Card")} •••• {card.lastNumbers || card.last4 || "****"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {card.cardholderName}
                      {card.expiration && ` • Exp: ${card.expiration}`}
                    </Typography>
                  </Box>
                  <CheckCircleIcon color="action" sx={{ opacity: 0.3 }} />
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* Saved Banks */}
      {hasBanks && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t("saved_banks", "Saved Bank Accounts")}
          </Typography>
          <Stack spacing={1.5}>
            {banks.map((bank) => (
              <Paper
                key={bank._id}
                variant="outlined"
                onClick={() => onSelect({
                  id: bank._id,
                  type: "ach",
                  isNew: false,
                  last4: bank.last4,
                  bankName: bank.bank_name,
                })}
                sx={{
                  p: 2,
                  cursor: "pointer",
                  borderWidth: 2,
                  borderColor: "grey.300",
                  borderRadius: 2,
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "primary.50",
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      backgroundColor: "grey.100",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AccountBalanceIcon sx={{ fontSize: 24, color: "grey.600" }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {bank.bank_name || t("bank_account", "Bank Account")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      •••• {bank.last4 || "****"}
                      {bank.account_type && ` • ${bank.account_type}`}
                    </Typography>
                  </Box>
                  <CheckCircleIcon color="action" sx={{ opacity: 0.3 }} />
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* Add New Payment Methods */}
      <Box>
        {hasSavedMethods && (
          <Divider sx={{ my: 3 }}>
            <Chip label={t("or_add_new", "Or Add New")} size="small" />
          </Divider>
        )}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          {cardPaymentAvailable && (
            <FriendlyButton
              variant="outlined"
              onClick={() => onSelect({ type: "card", isNew: true })}
              startIcon={<AddIcon />}
              sx={{ flex: 1, minHeight: 80, py: 2 }}
            >
              <CreditCardIcon sx={{ mr: 1 }} />
              {t("add_new_card", "Add New Card")}
            </FriendlyButton>
          )}
          {achPaymentAvailable && (
            <FriendlyButton
              variant="outlined"
              onClick={() => onSelect({ type: "ach", isNew: true })}
              startIcon={<AddIcon />}
              sx={{ flex: 1, minHeight: 80, py: 2 }}
            >
              <AccountBalanceIcon sx={{ mr: 1 }} />
              {t("add_new_bank", "Add Bank Account")}
            </FriendlyButton>
          )}
        </Stack>
      </Box>

      {!hasSavedMethods && !cardPaymentAvailable && !achPaymentAvailable && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t("no_payment_methods_available", "No payment methods are available for this organization.")}
        </Alert>
      )}
    </Box>
  );
};

export default PaymentMethodsList;
